import { ENV } from '../../config/env';
import { AUTH_STORAGE_KEYS } from '../auth/constants';
import { triggerSessionExpired, isSessionExpired } from '../auth/sessionExpiry';

/**
 * Unified API client that handles:
 * - Auth token injection (with expiry-safe reads)
 * - Response envelope unwrapping (handles all backend patterns)
 * - Consistent error handling
 *
 * Backend response patterns:
 *   messaging-service (Go):  { status: "success", data: { conversations: [...] } }
 *   candidate-service (Go):  { status: { error: false }, payload: { data: [...] } }
 *   matches-vectors (Go):    { matches: [...], totalCandidates: 5 }  (raw, no wrapper)
 *   signup-service (Go):     { status: "success", data: { ... } }
 */

// ── Helpers ──────────────────────────────────────────────────────────────────

function getAuthToken(): string | null {
    return localStorage.getItem(AUTH_STORAGE_KEYS.TOKEN);
}

function authHeaders(): HeadersInit {
    const token = getAuthToken();
    return {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
    };
}

/**
 * Unwraps backend response envelopes into the actual data payload.
 *
 * Handles three known patterns in priority order:
 *   1. { status, data: T }          → returns T   (messaging-service, signup-service)
 *   2. { status, payload: { data: T } } → returns T   (candidate-service)
 *   3. raw T                         → returns T   (matches-vectors)
 */
export function unwrapResponse<T = any>(json: any): T {
    // Pattern 1: { status: "success"|"error", data: T }
    if (json && typeof json === 'object' && 'status' in json && 'data' in json) {
        return json.data as T;
    }

    // Pattern 2: { status: {...}, payload: { data: T } }
    if (json && typeof json === 'object' && json.payload?.data !== undefined) {
        return json.payload.data as T;
    }

    // Pattern 3: raw response (matches-vectors, etc.)
    return json as T;
}

// ── Core request function ────────────────────────────────────────────────────

interface RequestOptions {
    method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
    body?: any;
    headers?: HeadersInit;
    /** Skip response unwrapping — return raw JSON */
    raw?: boolean;
}

/**
 * Make an authenticated API request with automatic response unwrapping.
 *
 * @example
 *   // GET with unwrapping
 *   const { conversations } = await apiRequest<{ conversations: DavidConversation[] }>(
 *       `${ENV.MESSAGING_SERVICE_URL}/admin/david-conversations`
 *   );
 *
 *   // POST with body
 *   const result = await apiRequest<SendAsDavidResponse>(
 *       `${ENV.MESSAGING_SERVICE_URL}/admin/send-as-david`,
 *       { method: 'POST', body: requestData }
 *   );
 */
export async function apiRequest<T = any>(
    url: string,
    options: RequestOptions = {}
): Promise<T> {
    const { method = 'GET', body, headers, raw = false } = options;

    const response = await fetch(url, {
        method,
        headers: { ...authHeaders(), ...headers },
        ...(body !== undefined && { body: JSON.stringify(body) }),
    });

    if (!response.ok) {
        // 401 = expired or invalid JWT. Instead of letting each component
        // show a random "HTTP 401" error, trigger the global session-expired
        // modal so the user gets a clean "please sign in again" overlay.
        if (response.status === 401 && !isSessionExpired()) {
            triggerSessionExpired();
        }

        const errorData = await response.json().catch(() => ({}));
        const message =
            errorData.error ||
            errorData.detail ||
            errorData.status?.msg ||
            `HTTP ${response.status}`;
        throw new Error(message);
    }

    // Some endpoints (e.g. email send) return no body
    const contentType = response.headers.get('content-type');
    if (!contentType?.includes('application/json')) {
        return undefined as T;
    }

    const json = await response.json();
    return raw ? json : unwrapResponse<T>(json);
}

// ── Pre-configured service clients ──────────────────────────────────────────

/** Messaging service (Go) — always wraps in { status, data } */
export const messagingApi = {
    get: <T>(path: string) =>
        apiRequest<T>(`${ENV.MESSAGING_SERVICE_URL}${path}`),
    post: <T>(path: string, body?: any) =>
        apiRequest<T>(`${ENV.MESSAGING_SERVICE_URL}${path}`, { method: 'POST', body }),
};

/** Candidate service (Go) — wraps in { status, payload: { data } } */
export const candidateApi = {
    get: <T>(path: string) =>
        apiRequest<T>(`${ENV.CANDIDATE_SERVICE_URL}${path}`),
    post: <T>(path: string, body?: any) =>
        apiRequest<T>(`${ENV.CANDIDATE_SERVICE_URL}${path}`, { method: 'POST', body }),
};

/** Matches/vectors service (Go) — returns raw JSON */
export const matchesApi = {
    get: <T>(path: string) =>
        apiRequest<T>(`${ENV.MATCHES_SERVICE_URL}${path}`, { raw: true }),
    post: <T>(path: string, body?: any) =>
        apiRequest<T>(`${ENV.MATCHES_SERVICE_URL}${path}`, { method: 'POST', body, raw: true }),
};

/** Signup service (Go) — wraps in { status, data } */
export const signupApi = {
    get: <T>(path: string) =>
        apiRequest<T>(`${ENV.API_BASE_URL}${path}`),
    post: <T>(path: string, body?: any) =>
        apiRequest<T>(`${ENV.API_BASE_URL}${path}`, { method: 'POST', body }),
};

/** Company service (Go) — wraps in { status, payload: { data } } */
export const companyApi = {
    get: <T>(path: string) =>
        apiRequest<T>(`${ENV.COMPANY_SERVICE_URL}${path}`),
    post: <T>(path: string, body?: any) =>
        apiRequest<T>(`${ENV.COMPANY_SERVICE_URL}${path}`, { method: 'POST', body }),
};

/** Job service (Go) — wraps in { status, payload: { data } } */
export const jobApi = {
    get: <T>(path: string) =>
        apiRequest<T>(`${ENV.JOB_SERVICE_URL}${path}`),
    post: <T>(path: string, body?: any) =>
        apiRequest<T>(`${ENV.JOB_SERVICE_URL}${path}`, { method: 'POST', body }),
};

/** David recruiter service (Python FastAPI) — returns raw JSON.
 *  Hosts the /recruiter-ai/v1/draft endpoint that powers the admin David inbox. */
export const davidRecruiterApi = {
    get: <T>(path: string) =>
        apiRequest<T>(`${ENV.DAVID_RECRUITER_URL}${path}`, { raw: true }),
    post: <T>(path: string, body?: any) =>
        apiRequest<T>(`${ENV.DAVID_RECRUITER_URL}${path}`, { method: 'POST', body, raw: true }),
};

// ── Score normalization utility ─────────────────────────────────────────────

/**
 * Normalize match score to 0-1 using weighted components.
 * Weights: Vector 50%, Qwen 35%, Soft criteria 15%
 */
export function normalizeMatchScore(
    vectorSimilarity: number,
    qwenScore: number,
    softCriteriaScore: number
): number {
    const v = (vectorSimilarity || 0) * 50;
    const q = (qwenScore || 0) / 100 * 35;
    const s = Math.min((softCriteriaScore || 0) / 5, 1) * 15;
    return Math.min(Math.max((v + q + s) / 100, 0), 1);
}
